import { openDB } from 'idb';

/**
 * IndexedDB-backed persistence for in-flight streaming uploads.
 *
 * Schema:
 *   uploads (keyPath: 'recordingId')
 *     {
 *       recordingId, userId, mediaType, itemId, sessionId,
 *       mimeType, createdAt, totalBytes,
 *       chunks: [{ index, uploaded, blob?, size, failureCount }],
 *       manifestUploaded, status, assembled
 *     }
 *
 * Blobs are stored inline on unuploaded chunks only. Once a chunk is
 * confirmed uploaded, its blob is dropped so the store stays small.
 */

const DB_NAME = 'ff-upload-persistence';
const DB_VERSION = 1;
const STORE = 'uploads';

let dbPromise = null;
function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'recordingId' });
        }
      }
    });
  }
  return dbPromise;
}

export async function putUpload(record) {
  const db = await getDb();
  await db.put(STORE, record);
}

export async function getUpload(recordingId) {
  const db = await getDb();
  return db.get(STORE, recordingId);
}

export async function deleteUpload(recordingId) {
  const db = await getDb();
  await db.delete(STORE, recordingId);
}

export async function listUploads() {
  const db = await getDb();
  return db.getAll(STORE);
}

// Merge a partial update into an existing record, or initialize it.
export async function updateUpload(recordingId, mutator) {
  const db = await getDb();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  const existing = (await store.get(recordingId)) || { recordingId };
  const next = typeof mutator === 'function' ? mutator(existing) : { ...existing, ...mutator };
  await store.put(next);
  await tx.done;
  return next;
}

// Mark a chunk as uploaded and drop its blob so IDB doesn't bloat.
export async function markChunkUploaded(recordingId, chunkIndex) {
  return updateUpload(recordingId, (rec) => {
    const chunks = (rec.chunks || []).map((c) =>
      c.index === chunkIndex ? { ...c, uploaded: true, blob: undefined } : c
    );
    return { ...rec, chunks };
  });
}

// Increment the failure counter on a chunk; uploader uses this for backoff.
export async function bumpChunkFailure(recordingId, chunkIndex, errorMessage) {
  return updateUpload(recordingId, (rec) => {
    const chunks = (rec.chunks || []).map((c) =>
      c.index === chunkIndex
        ? { ...c, failureCount: (c.failureCount || 0) + 1, lastError: errorMessage || null }
        : c
    );
    return { ...rec, chunks };
  });
}

// Append a freshly-captured chunk blob from MediaRecorder ondataavailable.
export async function appendChunk(recordingId, chunkIndex, blob) {
  return updateUpload(recordingId, (rec) => {
    const chunks = [...(rec.chunks || [])];
    const existing = chunks.find((c) => c.index === chunkIndex);
    const entry = {
      index: chunkIndex,
      uploaded: false,
      blob,
      size: blob?.size || 0,
      failureCount: 0
    };
    if (existing) {
      Object.assign(existing, entry);
    } else {
      chunks.push(entry);
    }
    return { ...rec, chunks };
  });
}

// Returns upload records that haven't been finalized (manifest written + server
// acknowledged). These are the candidates for resume-on-mount.
export async function listResumable() {
  const all = await listUploads();
  return all.filter((rec) => !rec.assembled);
}
